/**
 * Lightweight code tokenizer for Claude Code terminal display.
 * Returns an array of {text, type} tokens where type is one of:
 * 'keyword', 'string', 'comment', 'number', 'function', 'operator', 'punctuation', 'plain'
 *
 * Zero external dependencies — pure regex-based tokenization.
 */

const LANG_ALIASES = {
  javascript: 'js', typescript: 'ts', jsx: 'js', tsx: 'ts',
  python: 'py', python3: 'py', py: 'py', js: 'js', ts: 'ts',
  json: 'json', bash: 'bash', shell: 'bash', sh: 'bash', zsh: 'bash',
  diff: 'diff', patch: 'diff',
};

const JS_KW = new Set([
  'const', 'let', 'var', 'function', 'return', 'if', 'else', 'import', 'export',
  'from', 'async', 'await', 'class', 'new', 'this', 'null', 'undefined', 'true',
  'false', 'typeof', 'instanceof', 'switch', 'case', 'break', 'continue', 'default',
  'throw', 'try', 'catch', 'finally', 'for', 'while', 'do', 'of', 'in', 'yield',
]);

const PY_KW = new Set([
  'def', 'class', 'import', 'from', 'return', 'if', 'elif', 'else', 'for', 'while',
  'try', 'except', 'with', 'as', 'None', 'True', 'False', 'self', 'lambda', 'yield',
  'async', 'await', 'pass', 'break', 'continue', 'raise', 'finally', 'not', 'and',
  'or', 'in', 'is', 'del', 'global', 'nonlocal', 'assert',
]);

const BASH_KW = new Set([
  'if', 'then', 'else', 'elif', 'fi', 'for', 'while', 'do', 'done',
  'case', 'esac', 'function', 'return', 'exit', 'export', 'local', 'in',
]);

const OP_RE = /^(?:=>|===|!==|==|!=|<=|>=|&&|\|\||[+\-*/%=<>!&|^~])/;
const PUNCT_RE = /^[{}()\[\];:,.]/;
const NUM_RE = /^(?:0[xX][\da-fA-F]+|0[bB][01]+|0[oO][0-7]+|\d+\.?\d*(?:[eE][+-]?\d+)?)/;

function push(t, text, type) { if (text) t.push({ text, type }); }

// Shared engine for JS/TS and Python
function tokenizeCode(code, keywords, isPy) {
  const t = [];
  let i = 0;
  while (i < code.length) {
    const rest = code.slice(i);
    const c = rest[0];

    // Single-line comment
    if (c === '#' || rest.startsWith('//')) {
      const end = code.indexOf('\n', i);
      const s = end < 0 ? code.slice(i) : code.slice(i, end);
      push(t, s, 'comment'); i += s.length; continue;
    }
    // Multi-line comment /* */
    if (rest.startsWith('/*')) {
      const end = code.indexOf('*/', i + 2);
      const s = end < 0 ? code.slice(i) : code.slice(i, end + 2);
      push(t, s, 'comment'); i += s.length; continue;
    }
    // Python decorator
    if (isPy && c === '@' && (i === 0 || code[i - 1] === '\n')) {
      const m = rest.match(/^@[\w.]+/);
      if (m) { push(t, m[0], 'function'); i += m[0].length; continue; }
    }
    // Triple-quoted strings (Python)
    if (isPy) {
      const m = rest.match(/^[fFrRbBuU]*(?:"""[\s\S]*?"""|'''[\s\S]*?''')/);
      if (m) { push(t, m[0], 'string'); i += m[0].length; continue; }
    }
    // Python string prefix (f, r, b, u)
    if (isPy) {
      const m = rest.match(/^[fFrRbBuU]+(?=["'])/);
      if (m) { push(t, m[0], 'string'); i += m[0].length; continue; }
    }
    // Strings: single, double, backtick
    if (c === '"' || c === "'" || c === '`') {
      let j = 1;
      while (j < rest.length && rest[j] !== c) { if (rest[j] === '\\') j++; j++; }
      push(t, rest.slice(0, j + 1), 'string'); i += j + 1; continue;
    }
    // Numbers
    const nm = rest.match(NUM_RE);
    if (nm && (i === 0 || /[\s([\]{},;:=+\-*/%!<>&|^~]/.test(code[i - 1]))) {
      push(t, nm[0], 'number'); i += nm[0].length; continue;
    }
    // Identifiers / keywords
    const id = rest.match(/^[a-zA-Z_$][\w$]*/);
    if (id) {
      const w = id[0];
      push(t, w, keywords.has(w) ? 'keyword' : rest[w.length] === '(' ? 'function' : 'plain');
      i += w.length; continue;
    }
    // Operators
    const op = rest.match(OP_RE);
    if (op) { push(t, op[0], 'operator'); i += op[0].length; continue; }
    // Punctuation
    const pn = rest.match(PUNCT_RE);
    if (pn) { push(t, pn[0], 'punctuation'); i += 1; continue; }
    // Whitespace / fallback
    const ws = rest.match(/^\s+/);
    if (ws) { push(t, ws[0], 'plain'); i += ws[0].length; }
    else { push(t, c, 'plain'); i += 1; }
  }
  return t;
}

function tokenizeJSON(code) {
  const t = [];
  let i = 0;
  while (i < code.length) {
    const rest = code.slice(i);
    if (rest[0] === '"') {
      let j = 1;
      while (j < rest.length && rest[j] !== '"') { if (rest[j] === '\\') j++; j++; }
      const s = rest.slice(0, j + 1);
      push(t, s, /\s*:/.test(rest.slice(j + 1, j + 3)) ? 'function' : 'string');
      i += s.length;
    } else if (/^(?:true|false|null)\b/.test(rest)) {
      const m = rest.match(/^(?:true|false|null)/);
      push(t, m[0], 'keyword'); i += m[0].length;
    } else if (/^-?\d/.test(rest)) {
      const m = rest.match(/^-?\d+\.?\d*(?:[eE][+-]?\d+)?/);
      push(t, m[0], 'number'); i += m[0].length;
    } else if (/^[{}[\]:,]/.test(rest)) {
      push(t, rest[0], 'punctuation'); i += 1;
    } else {
      const ws = rest.match(/^\s+/);
      if (ws) { push(t, ws[0], 'plain'); i += ws[0].length; }
      else { push(t, rest[0], 'plain'); i += 1; }
    }
  }
  return t;
}

function tokenizeBash(code) {
  const t = [];
  let i = 0;
  while (i < code.length) {
    const rest = code.slice(i);
    if (rest[0] === '#') {
      const end = code.indexOf('\n', i);
      const s = end < 0 ? code.slice(i) : code.slice(i, end);
      push(t, s, 'comment'); i += s.length; continue;
    }
    if (rest[0] === '"' || rest[0] === "'") {
      const q = rest[0]; let j = 1;
      while (j < rest.length && rest[j] !== q) { if (rest[j] === '\\') j++; j++; }
      push(t, rest.slice(0, j + 1), 'string'); i += j + 1; continue;
    }
    const vm = rest.match(/^(?:\$\{[^}]*\}|\$[A-Za-z_]\w*)/);
    if (vm) { push(t, vm[0], 'number'); i += vm[0].length; continue; }
    if ('|><;&'.includes(rest[0])) {
      const op = rest.match(/^(?:>>|<<|&&|\|\||[|><;&])/);
      push(t, op[0], 'operator'); i += op[0].length; continue;
    }
    const id = rest.match(/^[a-zA-Z_][\w.\-]*/);
    if (id) {
      push(t, id[0], BASH_KW.has(id[0]) ? 'keyword' : 'plain');
      i += id[0].length; continue;
    }
    const ws = rest.match(/^\s+/);
    if (ws) { push(t, ws[0], 'plain'); i += ws[0].length; }
    else { push(t, rest[0], 'plain'); i += 1; }
  }
  return t;
}

function tokenizeDiff(code) {
  return code.split('\n').flatMap((line, idx, arr) => {
    const nl = idx < arr.length - 1 ? '\n' : '';
    if (line.startsWith('@@')) return [{ text: line + nl, type: 'function' }];
    if (line.startsWith('+')) return [{ text: line + nl, type: 'string' }];
    if (line.startsWith('-')) return [{ text: line + nl, type: 'keyword' }];
    return [{ text: line + nl, type: 'plain' }];
  });
}

/** Detect language from common file extensions or fenced code block language tags. */
export function detectLang(langTag) {
  if (!langTag) return 'plain';
  return LANG_ALIASES[langTag.toLowerCase().trim()] || 'plain';
}

/**
 * Tokenize source code into an array of {text, type} tokens.
 * @param {string} code - Source code to tokenize
 * @param {string} lang - Language tag (raw or pre-detected)
 * @returns {{text: string, type: string}[]}
 */
export function tokenize(code, lang) {
  switch (LANG_ALIASES[lang] || lang) {
    case 'js': case 'ts': return tokenizeCode(code, JS_KW, false);
    case 'py': return tokenizeCode(code, PY_KW, true);
    case 'json': return tokenizeJSON(code);
    case 'bash': return tokenizeBash(code);
    case 'diff': return tokenizeDiff(code);
    default: return [{ text: code, type: 'plain' }];
  }
}
